import UserLoading from 'publicContainer/Loading';
import React, { Suspense, lazy, useState } from 'react';
import { useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { DarkLoader } from 'zComponents/atoms';
import ErrorBoundary from './ErrorBoundary';
import RouteRenderer from './routeRenderer';
import { DASHBOARD_ROUTES, PUBLIC_ROUTES } from './routes';
import SocketManager from 'library/helpers/socketManager';
import SessionWatcher from './SessionWatcher';
import { consumeSignedOutMarker } from 'library/auth/authProvider';
import { isBillingOnlyProfile } from 'library/helpers/billingContact';

const FourZeroFour = lazy(() => import('publicContainer/404'));
const PartnerInviteAccept = lazy(() => import('publicContainer/PartnerInviteAccept'));
const AccessRefused = lazy(() => import('publicContainer/AccessRefused'));

const Navigation = () => {
	const userLoadingComplete = useSelector((state) => state.App.userLoadingComplete);
	const {
		isLoggedIn,
		loginLoading,
		signingOut,
		accessRefusal,
		selectedProfile: person
	} = useSelector((state) => state.Auth);

	// Read once per mount: the marker is one-shot, so a re-render must not consume it again.
	const [returningFromSignOut] = useState(consumeSignedOutMarker);

	// 🛑 BEFORE the routes. Sign-out clears the store synchronously while the provider logout is
	// still awaiting, so without this the app routes to /signin, loads its lazy chunk, paints the
	// Suspense loader — and only then does the browser leave for Kinde's logout endpoint.
	if (signingOut) {
		return <DarkLoader message='Signing out...' />;
	}

	// Show a full-page loader while the initial auth check is resolving
	// (e.g. cross-origin token exchange from Rooms Dashboard).
	//
	// 🛑 No message when we are coming BACK from a sign-out. The boot is the same either way, but
	// telling someone who just asked to leave that we are "Authenticating..." contradicts the
	// action they took — it reads as a second, unrelated wait rather than the tail of the first.
	if (loginLoading && !isLoggedIn) {
		return <DarkLoader message={returningFromSignOut ? undefined : 'Authenticating...'} />;
	}

	return (
		<ErrorBoundary>
			<Suspense fallback={<DarkLoader />}>
				{/*
				 * The `future={{ v7_startTransition, v7_relativeSplatPath }}` opt-in that used to sit here
				 * is gone: both are v7 DEFAULTS, and v7 warns about unrecognised future flags rather than
				 * ignoring them.
				 *
				 * ⭐ It is also why this upgrade is a version bump and nothing else. Those flags were
				 * adopted under v6, so the app has been running v7's routing behaviour — the
				 * `startTransition` state update and the relative-splat path resolution — in production
				 * for some time. The bump changes the package, not the semantics.
				 */}
				<BrowserRouter>
					{/* Not for a billing-only session (D48): a socket connect refreshes the propagation snapshot and the
					    access/booking/log listeners reload operational data the server refuses this person. */}
					{isLoggedIn && !isBillingOnlyProfile(person) && <SocketManager />}
					{/* Inside the router on purpose — it re-checks on route change, which is the
					    "clicks another page" half of noticing an expired session. */}
					{isLoggedIn && <SessionWatcher />}
					<Routes>
						<Route path='/'>
							{PUBLIC_ROUTES.map((route, index) => (
								<Route path={route.path} key={index} element={<route.component />} />
							))}
						</Route>
						{/* Partner invite accept — authenticated but role-less (a brand-new invitee has no
							roles yet), so it sits OUTSIDE the role-gated dashboard tree. The component itself
							handles the signed-out case. */}
						<Route path='partner-invite/accept' element={<PartnerInviteAccept />} />
						{/* If the user's resources are loading finished then render the restricted content.
							//? this also checks for the the user is logged in or not, if not then redirect to signin page.
						 */}
						{!userLoadingComplete && isLoggedIn ? (
							<Route path='*' element={accessRefusal ? <AccessRefused /> : <UserLoading />} />
						) : (
							<Route element={!isLoggedIn && <Navigate to={'signin'} replace />}>
								{DASHBOARD_ROUTES.map((route, index) => (
									<Route path={route.path} key={index} element={<route.component />}>
										{RouteRenderer({ person, routesList: route.routes })}
									</Route>
								))}
							</Route>
						)}

						{/* If the route reaches here then show the 404 screen as the path didn't mach any route. */}
						<Route path='/*' key={'404'} element={<FourZeroFour />} />
					</Routes>
				</BrowserRouter>
			</Suspense>
		</ErrorBoundary>
	);
};

export default Navigation;
